import {
  documentToHtmlString
} from '@contentful/rich-text-html-renderer'

import { BLOCKS, INLINES } from '@contentful/rich-text-types'

import { getComponent } from '../utils/constants/component-dictionary'
import {
  isValidUrl
} from '~/helpers/validator'

import {
  CALCULATORS
} from '~/constants/homeloans.constant'

import {
  FEATURED_IMAGE_DIMENSIONS
} from '~/constants/pages/contentful'

const MARKS = {
  BOLD: 'bold',
  ITALIC: 'italic',
  UNDERLINE: 'underline',
  CODE: 'code',
  SUPERSCRIPT: 'superscript',
  SUBSCRIPT: 'subscript'
}

// const { imageExt } = webpChecker()

export const RENDER_NODES = ({
  entriesMap,
  assetsMap,
  vertical,
  template,
  internalDomainForInlinesHyperlink,
  inlineComponents,
  author
} = {}) => {
  return {
    [BLOCKS.EMBEDDED_ENTRY]: (node, children) => {
      const entry = entriesMap.get(node.data.target.sys.id)

      const {
        componentId,
        componentClass,
        componentName,
        customComponent,
        keyPointsList,
        keyPointsTheme,
        ctaType,
        ctaId,
        ctaClass,
        ctaTitle,
        ctaSubtitle,
        ctaUrl,
        ctaTheme,
        ctaButtonLabel,
        __typename
      } = entry

      try {
        if (
          __typename === 'InlineComponent' &&
          customComponent &&
          typeof customComponent === 'object'
        ) {
          if (componentName === 'Vertical Form Widget') {
            if (!vertical) {
              // eslint-disable-next-line no-console
              console.error('Vertical is required!')
              return
            }

            return h(
              getKey(componentName.replace(/\s/g, ''), inlineComponents),
              {
                children,
                class: componentClass,
                vertical
              }
            )
          }

          const componentType = customComponent?.type
          const currentComponent = componentType
            ? getKey(componentType.replace(/\s/g, ''), inlineComponents)
            : componentType

          const map = getKey(componentType, {
            default: () => false,

            CALCULATOR: () => {
              const calculator = customComponent?.calculator

              if (!(calculator && CALCULATORS.includes(calculator))) {
                return
              }

              return h(
                currentComponent,
                {
                  class: `${componentClass} article-calculator`,
                  label: `${calculator} calculator`,
                  calculator: strToCamelCase(calculator)
                }
              )
            },

            CTA: () => {
              if (currentComponent) {
                return h(
                  currentComponent,
                  {
                    children,
                    class: 'my-6 lg:my-10 ' + componentClass,
                    id: componentId,
                    theme: customComponent.style,
                    align: customComponent?.align || 'center',
                    to: customComponent?.to,
                    full: template === 'ArticleWithSidebarV2',
                    widthFullContentDesktop: template === 'ArticleWithSidebarV2'
                  },
                  () => (customComponent.label)
                )
              }
            },

            'AUTHOR BLOCK': () => {
              return h(
                currentComponent,
                {
                  children,
                  class: 'my-6 lg:my-10 ' + componentClass,
                  id: componentId,
                  theme: 'expanded',
                  name: author?.name,
                  image: author?.authorImage?.url,
                  description: author?.authorProfile,
                  dimensions: {
                    width: author?.authorImage?.width ?? 200,
                    height: author?.authorImage?.height ?? 200
                  },
                  jobTitle: author?.authorJobTitle,
                  vertical: author?.vertical,
                  tips: author?.tips
                },
                () => (customComponent.label)
              )
            }
          })

          if (componentType && map?.()) {
            return map?.()
          }
        }

        if (__typename === 'InlineCta') {
          const theme = ctaTheme || 'default'
          let additionalProps = {
            title: ctaTitle,
            redirectUrl: ctaUrl,
            subtitle: documentToHtmlString(ctaSubtitle.json),
            ctaType,
            theme
          }

          if (ctaButtonLabel) {
            additionalProps = {
              ...additionalProps,
              buttonLabel: ctaButtonLabel
            }
          }

          const componentType = (() => {
            const _type = ctaType && ctaType.replace(/\s/g, '')

            if (ctaType === 'Default') { 
              return 'InlineCardCta'
            }

            if (ctaType === 'InlineAge') {
              return 'InlineAgeAbTest'
            }
            
            if (['Lifestage', 'State', 'AgeRange', 'Partner'].includes(_type)) {
              return _type
            }

            return 'MoleculesInlineLifeStage'
          })()
          
          const currentComponent = getKey(componentType, inlineComponents)

          return h(
            currentComponent,
            {
              children,
              class: `my-6 lg:my-10 ${ctaClass}`,
              id: ctaId,
              ...additionalProps
            }
          )
        }

        if (__typename === 'KeyPoints') {
          const theme = keyPointsTheme || 'default'
          const currentComponent = getKey(__typename, inlineComponents)

          return h(
            currentComponent,
            {
              children,
              class: 'my-6 lg:my-10 first:mt-0',
              document: keyPointsList.json,
              theme
            }
          )
        }

        if (__typename === 'FaqItem') {
          const currentComponent = getKey(__typename, inlineComponents)
          const title = (entry?.question?.json && documentToHtmlString(entry?.question?.json)) || null
          const content = (entry?.answer?.json && documentToHtmlString(entry?.answer?.json)) || null

          return h(
            currentComponent,
            {
              children,
              class: 'atoms-accordion my-6 lg:my-10',
              inGuides: true,
              title,
              content,
              border: false
            }
          )
        }

        if (__typename === 'TextBox') {
          const title = entry.title
          const content = entry.content
          const theme = entry.theme
          const _id = entry.id
          const _class = entry.class

          const currentComponent = getKey(__typename, inlineComponents)

          return h(
            currentComponent,
            {
              children,
              theme,
              class: `!px-0 my-6 lg:my-10 !py-0 ${_class} !bg-transparent`,
              id: _id,
              maxWidth: '',
              inGuides: true
            }, 
            {
              description: () => h('div', { innerHTML: documentToHtmlString(content.json) })
            }
          )
        }

        if (__typename === 'VideoAsset') {
          const currentComponent = getKey(__typename, inlineComponents)
          const videoUrl = entry.videoUrl
          const name = entry.name
          const video = entry.video

          return h(
            currentComponent,
            {
              children,
              class: 'my-6 lg:my-10',
              title: name,
              src: videoUrl ? videoUrl : video?.url
            }
          )
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error)
      }
    },

    [INLINES.HYPERLINK]: (node, key, next) => {
      try {
        const internalDomains = internalDomainForInlinesHyperlink
          ? internalDomainForInlinesHyperlink.split(',')
          : []

        const internalDomainscheck = internalDomains.filter(domain => node.data.uri.startsWith(domain))

        const target = internalDomainscheck.length > 0
          ? '_self'
          : '_blank'

        return h(
          getComponent('AtomsLink'),
          {
            key,
            to: node.data.uri,
            crossApp: true,
            target
          },
          () => next(node.content, key, next)
        )
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error)
      }
    },

    [BLOCKS.EMBEDDED_ASSET]: (node, key) => {
      if (assetsMap) {
        const asset = assetsMap.get(node.data.target.sys.id)

        if (asset) {
          return h(
            getComponent('AtomsImage'),
            {
              key,
              src: `${asset.url}?w=${1000}&h=${1000}`,
              height: asset.height,
              width: asset.width,
              alt: asset.description
            }
          )
        }
      }
    }
  }
}

export const RENDER_MARKS = {
  [MARKS.SUPERSCRIPT]: (text, key, h) => h('sup', { key }, text),
  [MARKS.SUBSCRIPT]: (text, key, h) => h('sub', { key }, text)
}

export const GET_FEATURE_IMAGE = (image, template = '', breakpoint = '') => {
  if (!image || typeof image === 'undefined') {
    return {}
  }

  const {
    width,
    height
  } = FEATURED_IMAGE_DIMENSIONS(template, breakpoint)

  const urlType = !isValidUrl(image.url) ? 'https:' : ''
  const url = `${urlType}${image.url}`
  // ?w=${width}&h=${height}&fm=${imageExt.value}

  return {
    url,
    title: image?.title,
    details: image?.description,
    description: image?.description,
    width,
    height
  }
}
